import { TemplatePrompts } from '@kimuno/api-sdk';
import type { Location } from 'found';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { recordTriggeredHint } from '../actions/user.actions';
import { onboardingHints, routes } from '../constants';
import { selectTriggeredHints } from '../reducers/user.selectors';
import useAction from './useAction';

// Literal twins of `promoBannerHintKey(podcastLaunchCampaignId)` and
// `LAUNCH_PARAM_KEYS.template`. This module is in AppPage's static import graph,
// and importing them would pull `AIAssistant/shared/utils` (module-scope
// `i18next.t`) in before AppPage's `i18next.init` runs, blanking every template
// title. The test suite pins both literals to the canonical constants.
const podcastBannerHintKey = 'promo-banner-podcast-launch';
const templateLaunchParamKey = 'template';

// Hints and banners that point the user at a destination, keyed by the exact
// `triggeredHints` key their visibility check reads. Reaching the destination
// concludes them for good, even if they were never displayed. Add an entry
// here for any new hint/banner that advertises a navigable target.
// Segment-boundary match: `/ki-tools` and `/ki-tools/<conversationId>` count,
// substring look-alikes (`/ki-toolsbar`, slugs containing "ki-tools") do not —
// a false positive would conclude a hint permanently.
const isOnAiToolsPage = ({ pathname }: Location) =>
	pathname === `/${routes.aiTools.root}` || pathname.startsWith(`/${routes.aiTools.root}/`);

export const targetRouteByHintKey: Record<string, (location: Location) => boolean> = {
	[onboardingHints.aiAssistant.imageGeneratorHint]: isOnAiToolsPage,
	[podcastBannerHintKey]: (location) =>
		isOnAiToolsPage(location) &&
		location.query?.[templateLaunchParamKey] === TemplatePrompts.CREATE_PODCAST,
};

export const useConcludeHintOnTargetRoute = (location: Location) => {
	const triggeredHints = useSelector(selectTriggeredHints);
	const recordTriggeredHintAction = useAction(recordTriggeredHint);

	// biome-ignore lint/correctness/useExhaustiveDependencies: recordTriggeredHintAction is recreated each render (useAction is unmemoized) but only wraps stable dispatch
	useEffect(() => {
		for (const [hintKey, isOnTargetRoute] of Object.entries(targetRouteByHintKey)) {
			if (!triggeredHints.includes(hintKey) && isOnTargetRoute(location)) {
				recordTriggeredHintAction(hintKey);
			}
		}
	}, [location, triggeredHints]);
};

export default useConcludeHintOnTargetRoute;
